import { Config } from 'config';

import { forEach, last, invokeMap } from 'lodash';

import { OwnedObject, cloneDeep, areEqual, invalidateOwner, Magnitude } from 'gear';

import { Xml } from '../util';
import { Point, Matrix, Frame, Boundary, BezierPoligonizer } from '../geometry';
import { Color, Pen, Brush, TextureBrush, PatternBrush } from '../style';
import { Process } from './process';
import { Figure, Region, mapCurvesToRegions, toSingleGShape } from '../figure';
import { Shape } from '../figure/shape';
import { kdTree } from 'kdTree';
import { TextLayout } from '../figure/text';

// CavitiesFilterProcess
//===============================================================================================

const CavitiesFilter = Process.CavitiesFilter = Process.extend({ typeName: 'CavitiesFilterProcess',
  type: 'CavitiesFilter',
  Properties: {
    minDiameter : { type: 'magnitude'  , def: null     , onChange: invalidateOwner, dropDef:true },
    quality     : { type: 'string'  , def: 'Default', onChange: invalidateOwner, dropDef:true }
  }
});
Xml.addFromBuild(CavitiesFilter);


// Border
//===============================================================================================

const Border = Process.Border = Process.extend({ typeName: 'BorderProcess',
  type: 'Border',
  Properties: {
    pen      : { type: 'Pen'     , def: new Pen({ color: Color.Black() }) , onChange: invalidateOwner },
    brush    : { type: 'Brush'   , def: Brush.empty , onChange: invalidateOwner },
    dX       : { type: 'magnitude'  , def: null    , onChange: invalidateOwner, dropDef:true },
    dY       : { type: 'magnitude'  , def: null    , onChange: invalidateOwner, dropDef:true },
    gap      : { type: 'magnitude'  , def: null    , onChange: invalidateOwner, dropDef:true },
    separate : { type: 'boolean' , def: false , onChange: invalidateOwner, dropDef:true }
  }
});
Xml.addFromBuild(Border);

// Contour
//===============================================================================================

const Contour = Process.Contour = Process.extend({ typeName: 'ContourProcess',
  type: 'Contour',
  Properties: {
    pen            : { type: 'Pen'      , def: Pen.empty                , onChange: invalidateOwner },
    brush          : { type: 'Brush'    , def: Brush.empty              , onChange: invalidateOwner },
    offset         : { type: 'magnitude', def: new Magnitude(0,'mm')    , onChange: invalidateOwner, dropDef:true },
    steps          : { type: 'integer'  , def: 0                        , onChange: invalidateOwner, dropDef:true },
    inside         : { type: 'boolean'  , def: false                    , onChange: invalidateOwner, dropDef:true },
    outside        : { type: 'boolean'  , def: true                     , onChange: invalidateOwner, dropDef:true },
    includeHoles   : { type: 'boolean'  , def: true                     , onChange: invalidateOwner, dropDef:true },
    combine        : { type: 'boolean'  , def: false                    , onChange: invalidateOwner, dropDef:true },
    lineJoin       : { type: 'string'   , def: 'Miter'                , onChange: invalidateOwner, dropDef:true },
    chamfer_D      : { type: 'number'   , def: 0.1                      , onChange: invalidateOwner, dropDef:true },
    chamfer_L      : { type: 'number'   , def: 0.1                      , onChange: invalidateOwner, dropDef:true },
    roundRadius    : { type: 'magnitude', def: new Magnitude(0.1, 'mm') , onChange: invalidateOwner, dropDef:true },
    roundMaxAngle  : { type: 'number'   , def: 120                      , onChange: invalidateOwner, dropDef:true },
    advancedSpacing: { type: 'boolean'  , def: false                    , onChange: invalidateOwner, dropDef:true },
    gap            : { type: 'number'   , def: 0.0                      , onChange: invalidateOwner, dropDef:true, doNotEncode:true }
  },
  init( c ) {
    // optional
    this.callParent();
    this.advancedSpacing = this.advancedSpacing || this.gap != 0.0;
  }, 
  
  canProcess: function(/* figure */) {
    return ( //=Config.contourFastPreview &&
             this.pen._empty &&
             this.brush instanceof Brush &&
             !(this.brush instanceof TextureBrush) &&
             !(this.brush instanceof PatternBrush) &&
             this.steps <= 1 &&
             ! this.inside && this.outside// && Contour.useFastPreviewFor(this)
              );
  },
  addPenContourExtraParams: function(p) {
    if ( p.lineJoin == 'Miter') {
      return Object.assign(p, { miterLimit: 3 });
    }
    else {
      return p ;
    }
  },
  process: function( c ) {
    var penContour = new PenContour({ pens: [ new Pen(this.addPenContourExtraParams({ width: this._offset*2
                                                      , color: this.brush.color
                                                      , lineJoin: this.lineJoin
                                                      }))
             
                                            ]
                                    , advancedSpacing: this.advancedSpacing
                                  });
    return penContour.process( c );
  }
});
Xml.addFromBuild(Contour);

// Apps can define to avoid fast preview depending on other contour properties
Contour.useFastPreviewFor = function(/* contour */) {
  return true;
};

// Pen Contour
//===============================================================================================

const createContourFigure = function( base, pen, size ) {
  const shape = base.clone();
  const frame = Frame.fromBounds(shape.bounds);
  forEach(shape.polyregions, p => {
    const renderMatrix = p._renderMatrix;
    const effectivePen = pen.clone();
    effectivePen.width = size * ( renderMatrix ? renderMatrix.scaleFactor() : 1 );
    p._renderFrame = frame.clone();
    p.pen = effectivePen;
  });

  return shape;
};

const getBaseWidth = function( shape ) {
  let maxInputWidth = 0;
  forEach( shape._polyregions, p => {
    const renderMatrix = p._renderMatrix;
    const inputWidth = p._pen.width * ( renderMatrix ? renderMatrix.scaleFactor() : 1 );
    if( inputWidth > maxInputWidth ) {
      maxInputWidth = inputWidth;
    }
  });
  return maxInputWidth;
};

const PenContour = Process.PenContour = Process.extend({ typeName: 'PenContourProcess',
  type: 'PenContour',
  Properties: { pens             : { type: 'Pen[]'  , def: []   , onChange: invalidateOwner },
                advancedSpacing  : { type: 'boolean', def: false, onChange: invalidateOwner, dropDef:true }
              },
  getColors( colors = [] ) {
    this.callParent(colors);
    forEach( this.pens, pen => {
      pen.getColors(colors);
    });
    return colors;
  },
  canProcess( /* figure */) {
    return Config.localPenContour;
  },
  nearEqual(a,b,eps=0.001) { return Math.abs(a-b)<=eps; },
  nearTableEqual(table, value)
  {
    let r;
    forEach(Object.keys(table), v => 
    {
      if ( this.nearEqual(v, value) && r === undefined )
        r = table[v];
    });
    return r ;
  },
  process(  c ) { // Figures: [ TextLayout, GShape, Raster ]
    const figures = c.figures;
    if( figures.length === 0 ) { return; } // I think we can remove this check
    var base = toSingleGShape(figures, true);

    // Remove powerclips from contour figure, they will just make everything slower
    base.removePowerClips();

    let size = getBaseWidth(base);

    let maxOffset = size;

    if ( this.advancedSpacing )
    {
      const pens = this._pens;
      for(let k = 0, kEnd = pens.length; k < kEnd; ++k ) 
      {
        const pen = pens[k];
        maxOffset += pen.width/2;
      }
    }

    let extraKerningChanges = 0 ;
    
    forEach( figures, textLayout  =>
    {
      if( textLayout instanceof TextLayout ) 
      {
        let shouldOutdate = false ;

        if ( this.advancedSpacing )
        {
          let lines = textLayout.textLines();

          let pairs        = [] ;
          let default_gapX = 0.0 ;
          let default_gapY = 0.0 ;

          const fontData = textLayout.text._fontData ;

          if ( fontData != null )
          {
            const table = fontData.metaData ;

            if ( table != null )
            {
              const fontSize = textLayout.text.properties.fontSize ;
  
              //const sizeTable = table[fontSize.toFixed(4)] ;

              const sizeTable = this.nearTableEqual(table, fontSize);
  
              if ( sizeTable !== undefined )
              {
                //const all_pairs = sizeTable[maxOffset.toFixed(4)] ;

                const all_pairs = this.nearTableEqual(sizeTable, maxOffset);

                if ( all_pairs !== undefined )
                {
                  for( let i = 0, c = all_pairs.length ; i < c ; ++ i )
                  {
                    const entry = all_pairs[i]; 
  
                    const tlc = entry['L'];
                    const trc = entry['R'];
                    const tsx = entry['S'];
                    const tsy = entry['Y'];
  
                    if ( tlc !== undefined && trc !== undefined && ( tsx !== undefined || tsy !== undefined ) )
                    {
                      pairs.push(entry) ;
                    }
                    else if ( tlc === undefined && trc === undefined && ( tsx !== undefined || tsy !== undefined ))
                    {
                      default_gapX = tsx !== undefined ? tsx : 0.0 ;
                      default_gapY = tsy !== undefined ? tsy : 0.0 ;
                    }
                  }
                }
              }
            }
          }

          for ( let li = 0, lc = lines.length ; li < lc ; ++ li )
          {
            const line = lines[li];
            
            const chars = line._chars ;

            for( let ci = 0, cc = chars.length, cl = chars.length - 1 ; ci < cl ; ++ ci )
            {
              const leftc  = chars[ci  ]._char;
              const rightc = chars[ci+1]._char;

              let extraKerningX = default_gapX ;
              let extraKerningY = default_gapY ;

              for( let i = 0, c = pairs.length ; i < c ; ++ i )
              {
                const entry = pairs[i]; 
                const tlc = entry['L'];
                const trc = entry['R'];
                const tsx = entry['S'];
                const tsy = entry['Y'];

                if ( tlc !== undefined && trc !== undefined && ( tsx !== undefined || tsy !== undefined ) )
                {
                  if ( tlc === leftc && trc === rightc )
                  {
                    extraKerningX = tsx !== undefined ? tsx : 0.0 ;
                    extraKerningY = tsy !== undefined ? tsy : 0.0 ;
                    break ;
                  }
                }
              }

              if ( extraKerningX != 0.0 || extraKerningY != 0.0 )
              {
                const extraKerning = Math.abs( Point.dotProduct(textLayout.text.effectiveLineDirection(), new Point(extraKerningX,extraKerningY) ) ) ;

                textLayout.charData(li+1,ci+2)._extraKerning = extraKerning ;                            

                ++ extraKerningChanges ;

                shouldOutdate = true ;
              }
            }
          }
        }

        if ( shouldOutdate )
        {
          textLayout.invalidate();
          textLayout.textLines();
        }
      }
    } ) ;

    if ( extraKerningChanges > 0 )
    {
      base = toSingleGShape(figures, true);
      base.removePowerClips();
    }

    const pens = this._pens;
    const contours = [];
    let behindFill = false;
    for(let k = 0, kEnd = pens.length; k < kEnd; ++k ) {
      const pen = pens[k];
      size += pen.width;
      let lFigure = createContourFigure( base, pen, size);
      if (pen._behindFill) {
        behindFill = true;
      }
      contours.push( lFigure );
    }

    if (behindFill) {
      for(let k = 0, kEnd = contours.length; k < kEnd; ++k ) {
        c.figures.unshift( contours[k] );
      }
    }
    else {
      for(let k = contours.length-1, kEnd = 0; k >= kEnd; --k ) {
        c.figures.push( contours[k] );
      }
    }
}
});
Xml.addFromBuild(PenContour);

// Shadow
//===============================================================================================

const Shadow = Process.Shadow = Process.extend({ typeName: 'ShadowProcess',
  type: 'Shadow',
  Properties: {
    pen          : { type: 'Pen'        , def: Pen.empty              , onChange: invalidateOwner },
    brush        : { type: 'Brush'      , def: Brush.empty            , onChange: invalidateOwner },
    offsetX      : { type: 'magnitude'  , def: new Magnitude(0,'mm')  , onChange: invalidateOwner, dropDef:true },
    offsetY      : { type: 'magnitude'  , def: new Magnitude(0,'mm')  , onChange: invalidateOwner, dropDef:true },
    gap          : { type: 'magnitude'  , def: new Magnitude(0,'mm')  , onChange: invalidateOwner, dropDef:true },
    simplify     : { type: 'boolean'    , def: false                  , onChange: invalidateOwner, dropDef:true }
  }
});
Xml.addFromBuild(Shadow);

// RasterShadow
//===============================================================================================

const RasterShadowGap = Process.RasterShadowGap = OwnedObject.extend({ typeName: 'RasterShadowGap',
  Properties: {
    pen: { type: 'Pen', def: Pen.empty, onChange: invalidateOwner }
  },
  getColors( colors = [] ) {
    this._pen.getColors(colors);
    return colors;
  }
});
Xml.addFromBuild(RasterShadowGap,'Gap');

const RasterShadow = Process.RasterShadow = Process.extend({ typeName: 'RasterShadowProcess',
  type: 'RasterShadow',
  Properties: {
    pen     : { type: 'Pen'             , def: Pen.empty            , onChange: invalidateOwner },
    brush   : { type: 'Brush'           , def: Brush.empty          , onChange: invalidateOwner },
    offsetX : { type: 'magnitude'       , def:new Magnitude(0,'mm') , onChange: invalidateOwner, dropDef:true },
    offsetY : { type: 'magnitude'       , def:new Magnitude(0,'mm') , onChange: invalidateOwner, dropDef:true },
    gap     : { type: 'RasterShadowGap' , def: null                 , onChange: invalidateOwner, dropDef:true, manualParsing:true }
  },
  getColors( colors = [] ) {
    this.callParent(colors);
    const gap = this._gap;
    if( gap ) {
      gap.getColors(colors);
    }
    return colors;
  },
  setupXml( node ) {
    Xml.setupProperties(node,this);
    const gap = this._gap;
    if( gap ) {
      gap.buildXml(node);
    }
  },
  canProcess( /*figure*/) {
    return Config.localRasterShadow;
  },
  process( c ) {
    const pen = this._pen, brush = this._brush, figures = c.figures;
    if( pen._empty && brush._empty ) { return; }

    const base = toSingleGShape(figures);

    // Remove powerclips from contour figure, they will just make everything slower
    // We may add an option later to include powerclips in the Base
    base.removePowerClips();

    const frame = Frame.fromBounds(base.vectorBounds());

    let gapFigure = null,
        effectiveGap = 0;

    const { gap } = this;
    if( gap ) {

      const maxInputWidth = getBaseWidth(base);

      const gapPen = gap._pen;
      const effectiveGapPen = gapPen.clone();
      effectiveGapPen.width = gapPen.width + maxInputWidth;

      effectiveGap = effectiveGapPen.width;

      gapFigure = base.clone();

      gapFigure.setRenderFrame(frame);

      const gapBrush = Brush({ color: gapPen.color });

      forEach( gapFigure._polyregions, p => {
        p._renderMatrix = null;
        p.pen = effectiveGapPen;
        p.brush = gapBrush;
      });

      figures.unshift( gapFigure );
    }

    const effectivePen   = pen ;
    const effectiveBrush = brush ;

    // [CHECK]
    /* This was in the engine but it shouldn't be triggered consistently
       because it is only looking at the GShape pen and brush and not
       at the polyregions
    if( effectivePen._empty && ! base._pen._empty && ! brush._emty ) {
      effectivePen = base._pen.clone();
      effectivePen.color = brush.color;
    }

    if( effectiveBrush._empty && ! base._brush._empty && ! pen._empty ) {
      effectiveBrush = base._brush.clone();
      effectivePen.color = pen.color;
    }
    */

    const t = Matrix.translate( this._offsetX + effectiveGap, this._offsetY + effectiveGap );

    base.setRenderFrame( frame.transformed(t) );

    forEach( base._polyregions, p => {
      p._renderMatrix = null;
      p.pen = effectivePen;
      p.brush = effectiveBrush;
      p.transform( t );
    });

    figures.unshift(base);
  }
});
RasterShadow.parseXml = function( node ) {
  const p = Xml.parseProperties(node,RasterShadow);
  const gapNode = Xml.child(node,'Gap');
  if( gapNode ) {
    p.gap = RasterShadowGap.parseXml(gapNode);
  }
  return new RasterShadow(p);
};
Xml.addFromBuild(RasterShadow);

// Effect3d
//===============================================================================================

const Effect3d = Process.Effect3d = Process.extend({ typeName: 'Effect3dProcess',
  type: 'Effect3d',
  Properties: {
    borderIntensity      : { type: 'number'     , def: 0.7                      , onChange: invalidateOwner },
    borderSize           : { type: 'magnitude'  , def: new Magnitude(1.0,'mm')  , onChange: invalidateOwner },
    contourOffset        : { type: 'magnitude'  , def: new Magnitude(1.0,'mm')  , onChange: invalidateOwner },
    contourRegularizeW   : { type: 'magnitude'  , def: new Magnitude(0.0,'mm')  , onChange: invalidateOwner },
    lightGap             : { type: 'magnitude'  , def: new Magnitude(2.0,'mm')  , onChange: invalidateOwner },
    lightIntensity       : { type: 'number'     , def: 0.7                      , onChange: invalidateOwner },
    lightSize            : { type: 'magnitude'  , def: new Magnitude(3.0,'mm')  , onChange: invalidateOwner },
    lightSource          : { type: 'string'     , def: 'Up'                     , onChange: invalidateOwner },
    shadowIntensity      : { type: 'number'     , def: 0.35                     , onChange: invalidateOwner },
    shadowSizePercentage : { type: 'number'     , def: 0.95                     , onChange: invalidateOwner },
    slopeIntensity       : { type: 'number'     , def: 0.3                      , onChange: invalidateOwner },
    slopeSize            : { type: 'magnitude'  , def: new Magnitude(1.5,'mm')  , onChange: invalidateOwner }
  }
});
Xml.addFromBuild(Effect3d);

// Extrude3d
//===============================================================================================

const Extrude3D = Process.Extrude3D = Process.extend({ typeName: 'Extrude3DProcess',
  type: 'Extrude3D',
  Properties: {
    pen            : { type: 'Pen'      , def: Pen  .empty            , onChange: invalidateOwner },
    brush          : { type: 'Brush'    , def: Brush.empty            , onChange: invalidateOwner },
    depth          : { type: 'magnitude', def: new Magnitude(0.0,'mm'), onChange: invalidateOwner },
    vanishingPoint : { type: 'Point'    , def: new Point(0,0)         , onChange: invalidateOwner },
    rotationX      : { type: 'angle'    , def: 0                      , onChange: invalidateOwner, dropDef: true },
    rotationY      : { type: 'angle'    , def: 0                      , onChange: invalidateOwner, dropDef: true } ,
    rotationZ      : { type: 'angle'    , def: 0                      , onChange: invalidateOwner, dropDef: true }
  }
});
Xml.addFromBuild(Extrude3D);


// SimStitch
//===============================================================================================

const SimStitch = Process.SimStitch = Process.extend({ typeName: 'SimStitchProcess',
  type: 'SimStitch',
  Properties: {
    pen                   : { type: 'Pen'       , def: Pen  .empty                , onChange: invalidateOwner },
    brush                 : { type: 'Brush'     , def: Brush.empty                , onChange: invalidateOwner },
    width                 : { type: 'magnitude' , def: new Magnitude(3.0,'mm')    , onChange: invalidateOwner },
    density               : { type: 'magnitude' , def: new Magnitude(2.0,'mm')    , onChange: invalidateOwner },
    overlap               : { type: 'number'    , def: 75.0                       , onChange: invalidateOwner },
    tackdown              : { type: 'magnitude' , def: new Magnitude(0.5,'mm')    , onChange: invalidateOwner },
    onlyStitching         : { type: 'boolean'   , def: false                      , onChange: invalidateOwner },
    transformIndependent  : { type: 'boolean'   , def: false                      , onChange: invalidateOwner },
    thickness             : { type: 'magnitude' , def: new Magnitude(0.5842,'mm') , onChange: invalidateOwner }
  },
    canProcess(/*figure*/){
    return false;
  }
});

Xml.addFromBuild(SimStitch);

// Engrave
//===============================================================================================

var collapseIntoSinglePolyregion = function( shape ) {

  var polyregions = shape._polyregions;

  var collapsedPolyregion = polyregions[0];

  // This process forces fillMode winding, in case it was CadX, it will
  // convert holes into regions
  collapsedPolyregion.setFillMode( 'Winding' );

  var curves = collapsedPolyregion.enumCurves();
  while( polyregions.length > 1 ) {
    var extraPolyregion = last(polyregions);
    var extraCurves = extraPolyregion.enumCurves();
    for( var c=0, cEnd = extraCurves.length; c < cEnd; ++c ) {
      curves.push(extraCurves[c]);
    }
    polyregions.pop();
  }

  collapsedPolyregion.regions = mapCurvesToRegions( curves );
};

// Gets the orientation of the outer most curve from an a list of curves
var outerMostOrientation = function( curves ) {
  var bottomCurve, bottomCurve_ly = Number.MAX_VALUE;
  for( var i = 0, iEnd = curves.length; i < iEnd; ++i ) {
    var curve = curves[i];
    var ly = curve.bounds.ly;
    if( ly < bottomCurve_ly ) {
      bottomCurve_ly = ly;
      bottomCurve = curve;
    }
  }
  return bottomCurve.orientation();
};



const Engrave = Process.Engrave = Process.extend({ typeName: 'EngraveProcess',
  type: 'Engrave',
  Properties: {
    // curves
  },
  init( c ) {
    this.callParent();
    this._curves = c.curves || [];
  },
  get curves() {
    return this._curves;
  },
  isEqual( other ) {
    return this.propertiesAreEqual(other) &&
           areEqual(this._curves,other._curves);
  },
  setupXml( node ) {
    Xml.setupProperties(node,this);
    var curves = this._curves;
    for(var k=0,kEnd=curves.length;k<kEnd;++k){
      curves[k].setupXml(Xml.appendElement(node,'Curve'));
    }
  },
  clone() {
    var c = new Engrave(this);
    c._curves = cloneDeep( this._curves );
    return c;
  },

  canProcess(/*figure*/){
    return true;
  },
  process( c ){

    if( this.curves.length === 0 ) {
      return;
    }

    var figures = c.figures;

    // Path is centered around the anchor, leave c.anchor untouched after this
    var curves = invokeMap( this.curves, 'transformed', Matrix.translate(c.anchor) );

    var shape = toSingleGShape(figures);

    // Every polyregion is collapsed into the first one, to support multi line
    // text and mutlipart text.
    collapseIntoSinglePolyregion( shape );

    var collapsedPolyregion = shape._polyregions[0];

    // Check if the path needs to be reversed

    var shapeOrientation = outerMostOrientation( collapsedPolyregion.enumCurves() ),
        curvesOrientation = outerMostOrientation( curves );

    var reverseCurves = ( shapeOrientation === curvesOrientation );

    for( var k = 0, kEnd = curves.length; k < kEnd; ++k ) {
      var curve = curves[k];
      if( reverseCurves ) {
        curve = curve.reversed();
      }

      collapsedPolyregion._regions.add( new Region([ curve ]) );
    }

    shape.setRenderFrame( shape.bounds );

    c.figures = [ shape ];
  }
});

Engrave.parseXml = function(node) {
  var p = Xml.parseProperties(node,Engrave);
  p.curves = Boundary.arrayFromXml(node,'Curve');
  return new Engrave(p);
};
Xml.addFromBuild(Engrave);


// OutlineRhinestone
//=============================================================================

/*var processHelperFunctionsIfNeeded = function(/*shape, curves or whatever* /) {
  // ...
};*/

var areOverlapping = function (rhinestone1, rhinestone2) {
  const dx = (rhinestone1.x-rhinestone2.x);
  const dy = (rhinestone1.y-rhinestone2.y);
  const dist2 = dx*dx+dy*dy
  const r1_plus_r2 = rhinestone1.radius + rhinestone2.radius;
  const r1_minus_r2 = rhinestone1.radius - rhinestone2.radius;
  return r1_minus_r2 * r1_minus_r2 <= dist2 && dist2 <= r1_plus_r2 * r1_plus_r2;
}

var noOverlapping = function (rhinestone, ctx) {
  var max_neighbors = Math.min(ctx.rhinestones.length, ctx.max_comparisons);
  var nearest = ctx.tree.nearest(rhinestone, max_neighbors);
  var rhinestones = Array.from(nearest, x => x[0]);
  var i = 0, len = rhinestones.length;
  while(i < len && !areOverlapping(rhinestone, rhinestones[i]))
      i++
  return i >= len;
}

var getRhinestone = function (center, config) {
  var circleBoundary = Boundary.createEllipse({center: center, height: config.diameter})
  var circle = Shape.createFromCurve(circleBoundary, {pen: config.pen, brush: config.brush}).toGShape();
  return {
           shape: circle,
           x: center.x,
           y: center.y,
           radius: config.diameter/2 + config.pen.width
         }
}

var addRhinestone = function (ctx, p, config) {
  var rhinestone = getRhinestone(p, config);
  if(ctx.rhinestones.length === 0) {
    ctx.rhinestones.push(rhinestone);
    ctx.tree.insert(rhinestone);
  }
  else if(noOverlapping(rhinestone, ctx)) {
    ctx.rhinestones.push(rhinestone);
    ctx.tree.insert(rhinestone);
  }
  return ctx;
}

var getRhinestonesOnShapeInterior = function(ctx, shape, config, shift) {
  var bounds = shape.bounds;
  var polyregions = shape.polyregions;
  var shiftDistance = 0;
  var separation = config.separation;
  var halfSeparation = separation / 2;
  for (var i = bounds.ly, iteration = 0, iEnd = bounds.hy; i < iEnd; i += separation, ++iteration) {
    if (shift) {
      shiftDistance = iteration % 2 == 0 ? 0 : halfSeparation;
    }
    for (var j = bounds.lx + shiftDistance, jEnd = bounds.hx; j < jEnd; j += separation) {
      var p = new Point(j , i);
      for (var k = 0, kEnd = polyregions.length; k < kEnd; ++k){
        if (polyregions[k].hitTest(p, new Matrix())){  
          ctx = addRhinestone(ctx, p, config);
          break;
        }
      }
    }
  }
  return ctx;
}

var getCurveRhinestones = function(ctx, curve, config) {
  var curveArcLength = curve.arcLength();
  if (curveArcLength > config.separation) {
    var separation = config.separation > 0.0 ? config.separation : 1;
    var j = 0;
    for (var i = 0, iEnd = curve.pieces.length; i < iEnd; i++) {
      var piece = curve.pieces[i];
      var pieceArcLength = piece.arcLength();
      while (j < pieceArcLength) {
        ctx = addRhinestone(ctx, piece.at(j), config)
        j += separation;
      }
      j -= pieceArcLength;
    }
  }
  return ctx;
}

var getRhinestonesOnShapeCurves = function(ctx, shape, config) {
  var polyregions = shape.polyregions;
  for (var i=0, iEnd = polyregions.length; i < iEnd; ++i) {
    var curves = polyregions[i].enumCurves();
    for ( var k=0, kEnd = curves.length; k < kEnd; ++k ) {
      ctx = getCurveRhinestones(ctx, curves[k], config);
    }
  }
  return ctx;
}

const Rhinestone = Process.Rhinestone = Process.extend({
  typeName: 'RhinestoneProcess',
  type: 'Rhinestone',
  Properties: {
    method          : { type: 'string'            , def:     'curve' , onChange: invalidateOwner, dropDef:true },
    separation      : { type: 'number'            , def:       1.5   , onChange: invalidateOwner, dropDef:true },
    size            : { type: 'number'            , def:       1.5   , onChange: invalidateOwner, dropDef:true },
    bFactor         : { type: 'number'            , def:       1.5   , onChange: invalidateOwner, dropDef:true },
    maxComparisons  : { type: 'number'            , def:       10    , onChange: invalidateOwner, dropDef:true },
    pen             : { type: 'Pen'               , def:   Pen.empty , onChange: invalidateOwner },
    brush           : { type: 'Brush'             , def: Brush.empty , onChange: invalidateOwner }
  },
  init( c ) {
    // optional
    this.callParent();
    this._curves = c.curves || [];
  },
  isEqual( other ) {
    // optional
  },
  setupXml( node ) {
    Xml.setupProperties(node,this);
  },
  clone() {
    var c = new Rhinestone(this);
    return c;
  },
  canProcess(/*figure*/){
    return true;
  },
  process( c ){ 
    if (c.rhinestone_ctx === undefined) {
      // create rhinestone context
      c.rhinestone_ctx = { 
        rhinestones: [],
        tree: new kdTree([], Point.distance2, ['x', 'y']),
        max_comparisons: this._maxComparisons
      }
    }
    else if (c.rhinestone_ctx.tree.balanceFactor() > this._bFactor) {
      // rebalance k-d tree
      c.rhinestone_ctx.tree = new kdTree(c.rhinestone_ctx.rhinestones,
                                           Point.distance2,
                                           ['x', 'y']);
    }
    var shape = toSingleGShape(c.figures);
    var config = {
       pen: this._pen,
       brush: this._brush,
       separation: this._separation,
       diameter: this._size
    }

    switch (this._method.toLowerCase()) {
      case 'mfill':
        c.rhinestone_ctx = getRhinestonesOnShapeInterior(c.rhinestone_ctx, shape, config, false);
        break;
      case 'sfill':
        c.rhinestone_ctx = getRhinestonesOnShapeInterior(c.rhinestone_ctx, shape, config, true);
        break;
      case 'curve': // default is curve
      default:
        c.rhinestone_ctx = getRhinestonesOnShapeCurves(c.rhinestone_ctx, shape, config);
        break;
    }
    var rhinestoneShapes = Array.from(c.rhinestone_ctx.rhinestones, r => r.shape);
    c.figures = [shape].concat(rhinestoneShapes);
    shape.setRenderFrame(shape.bounds);
  }
});

Rhinestone.parseXml = function(node) {
  var p = Xml.parseProperties(node, Rhinestone);
  return new Rhinestone(p);
};
Xml.addFromBuild(Rhinestone);


/** WEBPACK FOOTER **
 ** ./src/cdl/process/decorate.js
 **/